import { BaseSchemaItem, StructuredDataSchemaItem } from '@repo/shared/dtos/structured-data'
import { generateId } from '@repo/shared/uuid/uuid'
import { Button } from '@repo/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@repo/ui/dialog'
import { useT } from '@repo/ui/hooks'
import { ScrollArea } from '@repo/ui/scrollArea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@repo/ui/select'
import { PlusIcon } from 'lucide-react'
import { useState } from 'react'

import { ItemBooleanForm } from '@/components/admin/structure-data/schema/forms/ItemBooleanForm'
import { ItemDateForm } from '@/components/admin/structure-data/schema/forms/ItemDateForm'
import { ItemEnumForm } from '@/components/admin/structure-data/schema/forms/ItemEnumForm'
import { ItemMultiselectForm } from '@/components/admin/structure-data/schema/forms/ItemMultiselectForm'
import { ItemNumberForm } from '@/components/admin/structure-data/schema/forms/ItemNumberForm'
import { ItemStringForm } from '@/components/admin/structure-data/schema/forms/ItemStringForm'

export function ItemCreator({ onCreate }: { onCreate: (item: StructuredDataSchemaItem) => void }) {
  const { tc, t } = useT({ keyPrefix: 'admin.structuredData.types' })
  const [open, setOpen] = useState(false)
  const baseItem: BaseSchemaItem & {
    defaultValue: null
  } = {
    id: generateId(),
    label: '',
    type: 'string',
    instructionsToFillValue: '',
    defaultValue: null,
  }
  const [type, setType] = useState<StructuredDataSchemaItem['type']>('string')
  const handleTypeChange = (type: StructuredDataSchemaItem['type']) => {
    setType(type)
  }
  const handleCreate = (newItem: StructuredDataSchemaItem) => {
    onCreate(newItem)
    setOpen(false)
  }

  const Form = () => {
    switch (type) {
      case 'string':
        return <ItemStringForm item={{ ...baseItem, type: 'string' }} onUpdate={handleCreate} />
      case 'boolean':
        return <ItemBooleanForm item={{ ...baseItem, type: 'boolean' }} onUpdate={handleCreate} />
      case 'number':
        return <ItemNumberForm item={{ ...baseItem, type: 'number' }} onUpdate={handleCreate} />
      case 'date':
        return <ItemDateForm item={{ ...baseItem, type: 'date' }} onUpdate={handleCreate} />
      case 'enum':
        return (
          <ItemEnumForm
            item={{ ...baseItem, type: 'enum', enumValues: [] }}
            onUpdate={handleCreate}
          />
        )
      case 'multiselect':
        return (
          <ItemMultiselectForm
            item={{ ...baseItem, type: 'multiselect', enumValues: [] }}
            onUpdate={handleCreate}
          />
        )
      default:
        return null
    }
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <PlusIcon className="size-4" />
          {tc('create', { cfl: true })}
        </Button>
      </DialogTrigger>

      <DialogContent className="px-0 xl:px-0" disableOutsideClick>
        <DialogHeader className="px-4 xl:px-6">
          <DialogTitle>{tc('create', { cfl: true })}</DialogTitle>
          <DialogDescription>
            <Select onValueChange={handleTypeChange} value={type} defaultValue={'string'}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder={tc('select', { cfl: true })} />
              </SelectTrigger>
              <SelectContent position="popper">
                <SelectItem value="string">{t('string')}</SelectItem>
                <SelectItem value="enum">{t('enum')}</SelectItem>
                <SelectItem value="multiselect">{t('multiselect')}</SelectItem>
                <SelectItem value="number">{t('number')}</SelectItem>
                <SelectItem value="date">{t('date')}</SelectItem>
                <SelectItem value="boolean">{t('boolean')}</SelectItem>
              </SelectContent>
            </Select>
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[75vh] overflow-y-auto px-4 xl:px-6">
          <Form />
        </ScrollArea>
      </DialogContent>
    </Dialog>
  )
}
